import { useState, useEffect, useContext } from "react";

import { Box, Grid, Typography, Button } from "@mui/material";

import { AnimatedLogo, ToggleButtons } from "@aclymatepackages/atoms";

import PurchaseConfirmation from "./PurchaseConfirmation";
import PurchaseVolumeSelectionInput from "./PurchaseVolumeSelectionInput";
import SubscriptionConfigForm from "./SubscriptionConfigForm";
import SubscriptionConfirmation from "./SubscriptionConfirmation";

import SlideLayout from "../../layouts/SlideLayout";
import PurchaseDialogLayout from "../../layouts/PurchaseDialogLayout";
import Link from "../../atoms/mui/Link";
import PaymentInput from "../../modules/PaymentInput";

import { googlePurchaseTracking } from "../../../helpers/components/purchase";
import { StripeCustomerContext } from "../../../helpers/contexts/stripeCustomer";
import usePurchasingContext, {
  PurchasingContextProvider
} from "../../../helpers/contexts/purchasing";
import usePortfoliosContext from "../../../helpers/contexts/offsetsPortfolios";

const PURCHASE_MODE_OPTIONS = [
  { name: "One-time purchase", value: "one-time" },
  { name: "Subscribe (recurring)", value: "subscribe" }
];

const PurchaseSuccessBox = ({ transactionId, isCNaughtPurchase }) => {
  const { purchaseDollars, purchaseTons } = usePurchasingContext();
  const { selectedPortfolio } = usePortfoliosContext();
  const { primary_image_url: image } = selectedPortfolio || {};

  const { paymentMethod } = useContext(StripeCustomerContext);
  const { us_bank_account } = paymentMethod || {};

  useEffect(() => {
    googlePurchaseTracking(
      "purchase",
      { portfolio: selectedPortfolio, purchaseDollars, purchaseTons },
      {
        transaction_id: transactionId,
        value: purchaseDollars,
        currency: "dollars"
      }
    );
  }, [
    purchaseDollars,
    purchaseTons,
    transactionId,
    isCNaughtPurchase,
    selectedPortfolio
  ]);

  const bankPurchaseText =
    "When the bank payment is successful, this offset will show up in your account and we will send you an email receipt.";

  return (
    <PurchaseDialogLayout
      title="Congratulations! You've helped the planet."
      subtitle={`You've taken a meaningful step towards addressing your Climate Footprint and becoming net zero. ${
        us_bank_account ? bankPurchaseText : ""
      }`}
    >
      <Box p={2} position="relative">
        <Grid container spacing={2} alignItems="stretch">
          <Grid item sm={4}>
            <Box
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                paddingTop: "100%"
              }}
            />
          </Grid>
          <Grid item sm={8}>
            <Typography variant="h6" color="textPrimary">
              Buying offsets allows you to earn higher levels of Aclymate
              certifications
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              We recommend offsetting all of your emissions every month
            </Typography>
            <Grid container justifyContent="center">
              <Grid item>
                <Link href="/platform/company/badges">
                  <Button variant="contained" color="secondary">
                    Learn More
                  </Button>
                </Link>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </PurchaseDialogLayout>
  );
};

const SubscriptionSuccessBox = () => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { budgetDollars, frequency } = usePurchasingContext();
  const { primary_image_url: image, name: portfolioName } =
    selectedPortfolio || {};

  return (
    <PurchaseDialogLayout
      title="Your offset subscription is live."
      subtitle={`We will automatically purchase offsets from ${portfolioName} every ${frequency === "monthly" ? "month" : frequency === "quarterly" ? "quarter" : "year"}, up to your $${budgetDollars} budget. We'll email a reminder two weeks before each charge and a receipt after each purchase.`}
    >
      <Box p={2} position="relative">
        <Grid container spacing={2} alignItems="stretch">
          <Grid item sm={4}>
            <Box
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                paddingTop: "100%"
              }}
            />
          </Grid>
          <Grid item sm={8}>
            <Typography variant="h6" color="textPrimary">
              Manage your subscription anytime
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Pause, cancel, or change your budget on the My Subscriptions tab.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PurchaseDialogLayout>
  );
};

const PortfolioPurchaseLayout = ({
  setPurchasePortfolio,
  viewMode = "company"
}) => {
  const { paymentMethod, isPaymentMethodLoading } = useContext(
    StripeCustomerContext
  );
  const { paymentMethodId } = paymentMethod || {};
  const { selectedPortfolio } = usePortfoliosContext();
  const { name } = selectedPortfolio || {};

  const { purchaseMode, setPurchaseMode, budgetDollars } =
    usePurchasingContext();

  const [showNextStep, setShowNextStep] = useState(false);
  const [purchaseCompleted, setPurchaseCompleted] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [subscriptionCreated, setSubscriptionCreated] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(""); // eslint-disable-line no-unused-vars

  const isSubscribeMode = purchaseMode === "subscribe";
  const canShowSubscriptionNextStep = isSubscribeMode && budgetDollars > 0;

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={() => setPurchasePortfolio(null)}
      header={
        <Typography variant="h3">Purchase Credits from {name}</Typography>
      }
      content={
        <>
          {isPaymentMethodLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <AnimatedLogo />
            </Box>
          ) : (
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <ToggleButtons
                  value={purchaseMode}
                  onChange={setPurchaseMode}
                  buttons={PURCHASE_MODE_OPTIONS}
                />
              </Grid>
              <Grid item>
                {isSubscribeMode ? (
                  <SubscriptionConfigForm />
                ) : (
                  <PurchaseVolumeSelectionInput
                    showNextStep={showNextStep}
                    setShowNextStep={setShowNextStep}
                    viewMode={viewMode}
                  />
                )}
              </Grid>
              {isSubscribeMode && canShowSubscriptionNextStep && (
                <Grid item>
                  {!paymentMethodId ? (
                    <PaymentInput />
                  ) : (
                    <SubscriptionConfirmation
                      setSubscriptionCreated={setSubscriptionCreated}
                      setSubscriptionId={setSubscriptionId}
                    />
                  )}
                </Grid>
              )}
              {!isSubscribeMode && showNextStep && (
                <Grid item>
                  {!paymentMethodId ? (
                    <PaymentInput />
                  ) : (
                    <PurchaseConfirmation
                      purchaseCompleted={purchaseCompleted}
                      setPurchaseCompleted={setPurchaseCompleted}
                      setTransactionId={setTransactionId}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </>
      }
      leftContent={
        (purchaseCompleted && (
          <PurchaseSuccessBox transactionId={transactionId} />
        )) ||
        (subscriptionCreated && <SubscriptionSuccessBox />)
      }
    />
  );
};

const PortfolioPurchaseFlow = ({ viewMode = "company", ...props }) => (
  <PurchasingContextProvider>
    <PortfolioPurchaseLayout {...props} viewMode={viewMode} />
  </PurchasingContextProvider>
);

export default PortfolioPurchaseFlow;
